import React, { useContext, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';
import { TabEditPopover } from './tab_edit_popover';
import { PageButtonContext } from './tiptap_editor';
import { showToast } from '../lib/toast';

function bestPopoverLoc(rect) {
  if (!rect) return 'top';
  const POP_H = 220, POP_W = 240, PAD = 8, MARGIN = 8;
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const spaceRight = window.innerWidth - rect.right;
  const spaceLeft  = rect.left;
  if (spaceBelow >= POP_H + PAD + MARGIN) return 'top';    // popover below (preferred for inline)
  if (spaceAbove >= POP_H + PAD + MARGIN) return 'bottom'; // popover above
  if (spaceRight >= POP_W + PAD + MARGIN) return 'left';   // popover to the right
  return 'right';                                          // popover to the left (last resort)
}

function PageButtonView({ node, editor, selected, getPos }) {
  const { tabId } = node.attrs;
  const ctx = useContext(PageButtonContext);
  const doc = ctx.doc;
  const tab = doc?.tabs?.find(t => t.id === tabId);
  const editable = ctx.editable;

  const label = tab?.label || 'Untitled page';
  const pageId = tab?.pageIds?.[0];
  const colorCls = tab?.color ? `c-${tab.color}` : 'c-none';

  const wrapRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [popOpen, setPopOpen] = useState(false);
  const [popLoc, setPopLoc] = useState('top');
  const prevSelected = useRef(null);

  // Open the popover whenever selection transitions false -> true (editable
  // mode). Closing is driven by the popover's own outside-click handler, not
  // by selection — that way clicking away actually dismisses it even though
  // ProseMirror may retain the NodeSelection. The first effect tick is an
  // initialization pass (prevSelected starts as null) so we never auto-open
  // on mount, even when ProseMirror initializes selection on the atom.
  useEffect(() => {
    if (prevSelected.current === null) {
      prevSelected.current = selected;
      return; // initialization tick — never auto-open on mount
    }
    if (editable && selected && !prevSelected.current) {
      if (!(editor.state.selection instanceof NodeSelection)) {
        prevSelected.current = selected;
        return;
      }
      if (wrapRef.current) {
        const r = wrapRef.current.getBoundingClientRect();
        setRect(r);
        setPopLoc(bestPopoverLoc(r));
      }
      setPopOpen(true);
    }
    if (prevSelected.current && !selected) {
      setPopOpen(false);
    }
    prevSelected.current = selected;
  }, [selected, editable]);

  // ----- Read-only (preview / PDF) -----
  if (!editable) {
    const onClick = (e) => {
      e.preventDefault();
      if (ctx.onOpenPage && pageId) ctx.onOpenPage(tabId);
    };
    return (
      <NodeViewWrapper as="div" className="tt-page-button-wrapper">
        <a className={`tt-page-button tt-page-button--readonly ${colorCls}`}
           href={pageId ? `#page-${pageId}` : undefined}
           onClick={onClick}>
          <span className="tt-page-button__label">{label}</span>
        </a>
      </NodeViewWrapper>
    );
  }

  // ----- Editable -----
  const onUpdate = (patch) => {
    if ('label' in patch && ctx.onRenameInlineTab) {
      ctx.onRenameInlineTab(tabId, patch.label);
    }
    if ('color' in patch && ctx.onUpdateInlineTab) {
      ctx.onUpdateInlineTab(tabId, { color: patch.color });
    }
  };

  const onDelete = () => {
    if (!ctx.onDeleteInlineTab) return;
    ctx.onDeleteInlineTab(tabId);
  };

  return (
    <NodeViewWrapper className="tt-page-button-wrapper">
      <span ref={wrapRef}
            className={`tt-page-button ${colorCls}${selected ? ' tt-page-button--selected' : ''}`}
            data-drag-handle=""
            onMouseDown={() => {
              setTimeout(() => {
                if (wrapRef.current) {
                  const r = wrapRef.current.getBoundingClientRect();
                  setRect(r);
                  setPopLoc(bestPopoverLoc(r));
                }
                setPopOpen(true);
              }, 0);
            }}
            onDoubleClick={() => {
              setPopOpen(false);
              if (ctx.onOpenPage && pageId) ctx.onOpenPage(tabId);
            }}>
        <span className="tt-page-button__label">{label}</span>
        <svg className="tt-page-button__hint" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </span>
      {popOpen && rect && tab && createPortal(
        <TabEditPopover
          tab={tab}
          rect={rect}
          loc={popLoc}
          onClose={() => setPopOpen(false)}
          onUpdate={onUpdate}
          onDelete={onDelete}/>,
        document.body
      )}
    </NodeViewWrapper>
  );
}

export const PageButton = Node.create({
  name: 'pageButton',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      tabId: { default: null },
    };
  },

  addStorage() {
    return {
      doc: null,
      onOpenPage: null,
      onInsertInlineTab: null,
      onRenameInlineTab: null,
      onUpdateInlineTab: null,
      onDeleteInlineTab: null,
      onClonePageButtonSubtree: null,
      onRestoreInlineSubtree: null,
    };
  },

  parseHTML() {
    return [{
      tag: 'div[data-type="page-button"]',
      getAttrs(el) { return { tabId: el.getAttribute('data-tab-id') || null }; },
    }];
  },

  renderHTML({ HTMLAttributes }) {
    const { tabId, ...rest } = HTMLAttributes;
    return ['div', mergeAttributes(rest, {
      'data-type': 'page-button',
      'data-tab-id': tabId,
    })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PageButtonView);
  },

  addCommands() {
    return {
      insertPageButton: () => ({ editor, chain, state }) => {
        const tabId = editor.storage.pageButton?.onInsertInlineTab?.();
        if (!tabId) return false;
        const pos = state.selection.to;
        return chain().focus().insertContentAt(pos, { type: 'pageButton', attrs: { tabId } }).run();
      },
    };
  },

  addKeyboardShortcuts() {
    const intercept = (editor) => {
      const { state } = editor;
      const sel = state.selection;
      const onDelete = editor.storage.pageButton?.onDeleteInlineTab;
      if (!onDelete) return false;

      if (sel instanceof NodeSelection && sel.node.type.name === 'pageButton') {
        onDelete(sel.node.attrs.tabId);
        return true;
      }

      if (!sel.empty) {
        const tabIds = [];
        state.doc.nodesBetween(sel.from, sel.to, (node) => {
          if (node.type.name === 'pageButton' && node.attrs.tabId) {
            tabIds.push(node.attrs.tabId);
          }
        });
        if (tabIds.length > 0) {
          tabIds.forEach(id => onDelete(id, { silent: true }));
          showToast(
            tabIds.length === 1 ? 'Page button removed' : `${tabIds.length} page buttons removed`,
            { type: 'info' }
          );
        }
      }
      return false;
    };
    return {
      'Backspace': ({ editor }) => intercept(editor),
      'Delete': ({ editor }) => intercept(editor),
    };
  },
});
